<script setup lang="ts">
import { computed, ref } from 'vue'
import { useTaskStore } from '@/stores/tasks'
import { useUiStore } from '@/stores/ui'
import AppIcon from '@/components/AppIcon.vue'
import AppDrawer from '@/components/ui/AppDrawer.vue'
import { formatDateTime } from '@/utils/format'

defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()
const tasks = useTaskStore()
const ui = useUiStore()
const filter = ref('all')

const filtered = computed(() => tasks.tasks.filter(task => filter.value === 'all' || (filter.value === 'running' ? ['进行中', '排队中'].includes(task.status) : task.status === '已失败' || task.status === '部分失败')))

function retry(id: string): void {
  tasks.retry(id)
  ui.toast('info', '任务已重新加入队列', '进度会在当前页面持续更新。')
}

function cancel(id: string): void {
  tasks.cancel(id)
  ui.toast('warning', '任务已取消')
}
</script>

<template>
  <AppDrawer :open="open" title="任务中心" subtitle="导出、导入、批量操作和异步任务只保存在当前浏览器内存" width="460px" @close="emit('close')">
    <div class="segmented" role="tablist" aria-label="任务筛选">
      <button type="button" class="segmented__item" :class="{ 'segmented__item--active': filter === 'all' }" @click="filter = 'all'">全部</button>
      <button type="button" class="segmented__item" :class="{ 'segmented__item--active': filter === 'running' }" @click="filter = 'running'">进行中</button>
      <button type="button" class="segmented__item" :class="{ 'segmented__item--active': filter === 'failed' }" @click="filter = 'failed'">失败</button>
    </div>
    <div v-if="!filtered.length" class="empty-state">
      <span class="empty-state__art"><AppIcon name="task" :size="34" /></span>
      <h3 class="empty-state__title">暂无任务</h3>
      <p class="empty-state__text">导出、批量操作或导入后会在这里展示进度和失败重试入口。</p>
    </div>
    <div v-else class="task-list">
      <article v-for="task in filtered" :key="task.id" class="card card--pad task-row">
        <div class="task-row__head">
          <span class="task-row__title">{{ task.title }}</span>
          <span class="tag" :class="task.status === '已完成' ? 'tag--success' : task.status === '已失败' || task.status === '部分失败' ? 'tag--danger' : task.status === '已取消' ? 'tag--neutral' : 'tag--info'">{{ task.status }}</span>
        </div>
        <div class="task-row__meta">{{ task.taskNo }} · {{ task.taskType }} · {{ formatDateTime(task.createdAt) }}</div>
        <div class="progress"><span class="progress__bar" :class="{ 'progress__bar--danger': task.status === '已失败' || task.status === '部分失败' }" :style="{ width: `${task.progress}%` }" /></div>
        <div class="task-row__meta">{{ task.success }}/{{ task.total }} 项完成<template v-if="task.failed"> · {{ task.failed }} 项失败</template> · {{ task.progress }}%</div>
        <p v-if="task.message" class="muted">{{ task.message }}</p>
        <div class="task-row__actions">
          <button v-if="task.retryable" type="button" class="btn btn--sm" @click="retry(task.id)"><AppIcon name="refresh" :size="13" />重试失败项</button>
          <button v-if="['进行中', '排队中'].includes(task.status)" type="button" class="btn btn--sm btn--ghost" @click="cancel(task.id)">取消</button>
        </div>
      </article>
    </div>
  </AppDrawer>
</template>
